import { useState } from 'react';

interface Props {
  formula: string;
}

// FormulaPill renders an Excel formula like `=SUMIFS(A:A, B:B, "x")` as
// a compact inline pill. Hover reveals a breakdown: function name plus
// each argument labeled. The split respects nesting + string literals
// so `=IF(A1>0, "a,b", 0)` still splits into [A1>0, "a,b", 0].
export function FormulaPill({ formula }: Props) {
  const [open, setOpen] = useState(false);
  const parsed = parseFormula(formula);

  if (!parsed) {
    // Bare formula or parse failed — render as a plain code span so we
    // still display, just without the arg breakdown.
    return (
      <code className="rounded bg-primary/10 px-1.5 py-0.5 font-mono text-xs text-primary">
        {formula}
      </code>
    );
  }

  return (
    <span
      className="relative inline-block"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <code className="rounded bg-primary/10 px-1.5 py-0.5 font-mono text-xs text-primary cursor-help border border-primary/20">
        <span className="font-semibold">={parsed.fn}</span>
        <span>(</span>
        {parsed.args.map((a, i) => (
          <span key={i}>
            {i > 0 && <span className="text-primary/60">, </span>}
            <span className="underline decoration-dotted decoration-primary/40">{a}</span>
          </span>
        ))}
        <span>)</span>
      </code>
      {open && parsed.args.length > 0 && (
        <div
          role="tooltip"
          className="absolute left-0 top-full z-10 mt-1 w-72 rounded-md border border-border bg-white p-2 text-[11px] shadow-lg dark:border-border-dark dark:bg-surface-dark"
        >
          <div className="mb-1 font-semibold text-primary">{parsed.fn}</div>
          <ul className="space-y-1">
            {parsed.args.map((a, i) => (
              <li key={i} className="flex gap-2">
                <span className="shrink-0 text-text-secondary">Arg {i + 1}:</span>
                <code className="truncate font-mono text-text-primary dark:text-text-primary-dark">
                  {a}
                </code>
              </li>
            ))}
          </ul>
        </div>
      )}
    </span>
  );
}

// parseFormula extracts (fn, args) from an Excel formula string. Handles
// nested parens and double-quoted strings so commas inside them don't
// split args. Returns null if the formula doesn't match `=fn(args)`.
export function parseFormula(raw: string): { fn: string; args: string[] } | null {
  const s = raw.trim();
  if (!s.startsWith('=')) return null;
  const open = s.indexOf('(');
  if (open < 2) return null;
  if (!s.endsWith(')')) return null;

  const fn = s.slice(1, open).trim();
  // Function names are [A-Z0-9._] — reject garbage.
  if (!/^[A-Z][A-Z0-9._]*$/i.test(fn)) return null;

  const body = s.slice(open + 1, -1);
  if (body.trim() === '') return { fn, args: [] };

  const args: string[] = [];
  let depth = 0;
  let inStr = false;
  let start = 0;
  for (let i = 0; i < body.length; i++) {
    const ch = body[i];
    if (inStr) {
      if (ch === '"' && body[i + 1] === '"') {
        i++; // escaped ""
        continue;
      }
      if (ch === '"') inStr = false;
      continue;
    }
    if (ch === '"') {
      inStr = true;
      continue;
    }
    if (ch === '(' || ch === '{') depth++;
    else if (ch === ')' || ch === '}') depth--;
    else if (ch === ',' && depth === 0) {
      args.push(body.slice(start, i).trim());
      start = i + 1;
    }
  }
  args.push(body.slice(start).trim());
  return { fn, args };
}
